import { useEffect, useRef, useState } from 'react';

/**
 * Types `text` out one character at a time once `start` turns true.
 *
 * The caller renders the full text until typing begins, so the finished
 * height can be measured and reserved before the element is emptied. Typed
 * a character at a time it otherwise grows from nothing to three lines and
 * shoves the wordmark below it down the page as it goes.
 *
 * @returns {{ typed: string, isTyping: boolean, done: boolean }}
 */
export function useTypewriter(text, { start, speed = 18, holdAfter = 450 } = {}) {
  const [typed, setTyped] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!start || startedRef.current) return undefined;
    startedRef.current = true;
    setIsTyping(true);

    let index = 0;
    let timer;

    const tick = () => {
      index += 1;
      setTyped(text.slice(0, index));
      if (index < text.length) {
        timer = setTimeout(tick, speed);
      } else {
        timer = setTimeout(() => {
          setIsTyping(false);
          setDone(true);
        }, holdAfter);
      }
    };

    tick();
    return () => clearTimeout(timer);
  }, [start, text, speed, holdAfter]);

  return { typed, isTyping, done };
}
